import {
  ContentMediumType,
  JUNGLE_CLOUDFLARE_BASE_URL,
  UserData,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import {
  FileUploadError,
  FileUploadErrorCode,
  FileUploadInputMethod,
  FileUploadStage,
  PresignedPostPayload,
  StorageTransportRecovery,
  StorageUploadProgress,
  getPresignedPostBucketName,
  requestFileUploadUrl,
  uploadFileToPresignedPost,
  uploadFileToPresignedPostThroughCloudflare,
} from "./file-upload-client";

const loadDocumentUploadSentry = () => import("./document-upload-sentry");

// Getting a file into S3: a presigned POST from the worker, then the upload itself (with the Cloudflare
// proxy fallback and the transport-recovery reports the app relies on). These two steps are shared by the
// app's uploads (helperMethodsForHomePage.tsx) and the marketing site's landing page, which is why they
// are not in helperMethodsForHomePage: that module brings the app's stores and Firebase with it, and the
// landing page must load without them.

// Helper function to convert SVG to PNG
const convertSvgToPng = async (svgFile: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const svgString = e.target?.result as string;

      // Create an image element
      const img = new Image();

      const handleLoad = () => {
        // Create canvas
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas 2D rendering is unavailable"));
          return;
        }

        // Get actual SVG dimensions or use reasonable defaults
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;

        // If dimensions are still not available, try to parse SVG viewBox or use defaults
        if (!width || !height || width === 0 || height === 0) {
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
          const svgElement = svgDoc.querySelector("svg");

          if (svgElement) {
            // Try to get width and height attributes
            const svgWidth = svgElement.getAttribute("width");
            const svgHeight = svgElement.getAttribute("height");

            if (svgWidth && svgHeight) {
              width = parseFloat(svgWidth);
              height = parseFloat(svgHeight);
            } else {
              // Try to get viewBox dimensions
              const viewBox = svgElement.getAttribute("viewBox");
              if (viewBox) {
                const [, , vbWidth, vbHeight] = viewBox.split(" ").map(Number);
                width = vbWidth || 800;
                height = vbHeight || 600;
              } else {
                // Use aspect ratio preserving defaults
                width = 800;
                height = 600;
              }
            }
          } else {
            width = 800;
            height = 600;
          }
        }

        // Ensure minimum dimensions for quality
        const minSize = 300;
        if (width < minSize || height < minSize) {
          const scale = minSize / Math.min(width, height);
          width *= scale;
          height *= scale;
        }

        // Bound attacker-controlled or malformed SVG dimensions before canvas
        // allocation. Preserve aspect ratio while keeping useful OCR detail.
        const maxDimension = 4096;
        if (width > maxDimension || height > maxDimension) {
          const scale = maxDimension / Math.max(width, height);
          width *= scale;
          height *= scale;
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Fill with white background to handle transparent SVGs
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, width, height);

        // Draw the image on canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create new File object with PNG format
              const pngFileName = svgFile.name.replace(/\.svg$/i, ".png");
              const pngFile = new File([blob], pngFileName, {
                type: "image/png",
                lastModified: Date.now(),
              });
              resolve(pngFile);
            } else {
              reject(new Error("Failed to convert SVG to PNG"));
            }
          },
          "image/png",
          0.9
        ); // 0.9 quality for PNG
      };

      const handleError = () => {
        reject(new Error("Failed to load SVG image"));
      };

      img.onload = handleLoad;
      img.onerror = handleError;

      // Create blob URL for the SVG
      const svgBlob = new Blob([svgString], { type: "image/svg+xml" });
      const svgUrl = URL.createObjectURL(svgBlob);
      img.src = svgUrl;

      // Clean up blob URL after image loads or fails
      const cleanup = () => URL.revokeObjectURL(svgUrl);
      img.addEventListener("load", cleanup, { once: true });
      img.addEventListener("error", cleanup, { once: true });
    };

    reader.onerror = () => {
      reject(new Error("Failed to read SVG file"));
    };

    reader.readAsText(svgFile);
  });
};

export const getUrlForFileUploadToS3 = async (
  formattedFileName: string,
  user: UserData,
  contentMediumType: ContentMediumType | null,
  ingestionAttemptId?: string,
  cancellationSignal?: AbortSignal
): Promise<PresignedPostPayload> => {
  return requestFileUploadUrl({
    url: `${JUNGLE_CLOUDFLARE_BASE_URL}/file_or_url/generate_url_for_file_upload_to_s3`,
    body: {
      file_name: formattedFileName,
      user_id: user?.id,
      content_medium_type: contentMediumType,
      ingestion_attempt_id: ingestionAttemptId,
    },
    cancellationSignal,
  });
};

export const uploadFileToS3 = async (
  file: File | Blob,
  urlForFileUpload: PresignedPostPayload,
  user: UserData,
  contentMediumType: ContentMediumType | null,
  attemptId?: string,
  options?: {
    cancellationSignal?: AbortSignal;
    onUploadProgress?: (progress: StorageUploadProgress) => void;
    /** Actual file size after preparation, excluding multipart form fields. */
    onUploadFilePrepared?: (totalBytes: number) => void;
    preferProgressAwareTransport?: boolean;
    inputMethod?: FileUploadInputMethod;
    /** Called after a successful upload that needed a recovery transport or an in-memory copy. */
    onStorageTransportRecovery?: (recovery: StorageTransportRecovery) => void;
  }
): Promise<"success"> => {
  // Fetched now, not when a recovery has to be reported: the landing page leaves for the app right after
  // the upload, and a report started only then could be cut off by the navigation.
  void loadDocumentUploadSentry().catch(() => {});

  let fileToUpload = file;

  // Convert SVG to PNG for transparent processing.
  if (file instanceof File && (file.type === "image/svg+xml" || /\.svg$/i.test(file.name))) {
    try {
      fileToUpload = await convertSvgToPng(file);
    } catch (error) {
      throw new FileUploadError({
        message: "The SVG could not be converted to PNG",
        stage: FileUploadStage.ValidateInput,
        code: FileUploadErrorCode.FilePreparation,
        retryable: false,
        cause: error,
      });
    }
  }

  options?.onUploadFilePrepared?.(fileToUpload.size);
  await uploadFileToPresignedPost({
    file: fileToUpload,
    presignedPost: urlForFileUpload,
    preferXhrUpload: options?.preferProgressAwareTransport === true,
    cancellationSignal: options?.cancellationSignal,
    onUploadProgress: options?.onUploadProgress,
    cloudflareProxyUploadImpl: (fallbackOptions) => {
      const proxyUrl = new URL(`${JUNGLE_CLOUDFLARE_BASE_URL}/file_or_url/upload_file_to_s3_proxy`);
      proxyUrl.searchParams.set("target_url", urlForFileUpload.url);
      proxyUrl.searchParams.set("uploaded_file_object_key", urlForFileUpload.fields.key);
      if (attemptId) proxyUrl.searchParams.set("ingestion_attempt_id", attemptId);
      return uploadFileToPresignedPostThroughCloudflare({
        ...fallbackOptions,
        proxyUrl: proxyUrl.toString(),
        preferXhrUpload: options?.preferProgressAwareTransport === true,
      });
    },
    onStorageTransportRecovery: (recovery) => {
      options?.onStorageTransportRecovery?.(recovery);
      const {
        transport,
        requestAttemptsBeforeFallback,
        storageRequestId,
        storageExtendedRequestId,
        bodyMaterialized,
      } = recovery;
      if (!(file instanceof File) || !attemptId) return;
      // A recovery is rare; its Sentry reporting (and the SDK behind it) is fetched only then, which keeps
      // the marketing site's landing page, which shares this upload path, free of Sentry at first paint.
      loadDocumentUploadSentry().then(
        ({ reportDocumentUploadTransportRecovery, reportDocumentUploadBodyMaterializedRecovery }) => {
          reportDocumentUploadTransportRecovery({
            file,
            contentMediumType,
            user,
            bucketName: getPresignedPostBucketName(urlForFileUpload),
            objectKey: urlForFileUpload.fields.key || null,
            attemptId,
            requestAttemptsBeforeFallback,
            storageRequestId,
            storageExtendedRequestId,
            storageTransport: transport,
            bodyMaterialized,
          });
          if (bodyMaterialized) {
            reportDocumentUploadBodyMaterializedRecovery({
              file,
              contentMediumType,
              user,
              inputMethod: options?.inputMethod ?? "unknown",
              bucketName: getPresignedPostBucketName(urlForFileUpload),
              objectKey: urlForFileUpload.fields.key || null,
              attemptId,
              requestAttemptsBeforeFallback,
              storageTransport: transport,
            });
          }
        },
        () => {}
      );
    },
  });
  return "success";
};
